import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import {
  getProductTranslations,
  getRunTranslations,
  translateRun,
} from '../services/catalog-operations.service';

/** Poll cadence while a translate run may still be landing rows. */
export const TRANSLATIONS_POLL_MS = 5000;

/**
 * Which locales a run has been translated into. `poll` re-reads every few seconds — on while
 * the operator is watching a translate run land, off otherwise.
 */
export function useRunTranslations(workflowId: string, { enabled = true, poll = false } = {}) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.runTranslations(workflowId),
    queryFn: () => getRunTranslations(workflowId),
    enabled: enabled && workflowId !== '',
    refetchInterval: poll ? TRANSLATIONS_POLL_MS : false,
  });
}

/** Translate a run's enriched English into more locales. Invalidates the summary on accept. */
export function useTranslateRun(workflowId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locales, productIds }: { locales: string[]; productIds?: string[] }) =>
      translateRun(workflowId, locales, productIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.runTranslations(workflowId),
      });
    },
  });
}

/** One product's content in every stored locale. */
export function useProductTranslations(workflowId: string, productId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.productTranslations(workflowId, productId),
    queryFn: () => getProductTranslations(workflowId, productId),
    enabled: enabled && workflowId !== '' && productId !== '',
  });
}
